import type { MemberRecord } from './api'
import { canResend } from './member'

export type ResendWhat = 'receipt' | 'welcome' | 'renewal_link' | 'sign_in_link'

export const RESEND_ITEMS: { what: ResendWhat; label: string; sent: string }[] = [
  { what: 'receipt', label: 'Receipt', sent: 'Receipt' },
  { what: 'welcome', label: 'Welcome email', sent: 'Welcome email' },
  { what: 'renewal_link', label: 'Renewal link', sent: 'Renewal link' },
  { what: 'sign_in_link', label: 'Sign-in link', sent: 'Sign-in link' },
]

/** What Resend offers: everything, or only a sign-in link when the address
 * is blocked by an opt-out (the member asked for the link). */
export function resendItems(m: MemberRecord): typeof RESEND_ITEMS {
  if (canResend(m)) return RESEND_ITEMS
  return m.sign_in_link_ok ? RESEND_ITEMS.filter((i) => i.what === 'sign_in_link') : []
}

/** Why Resend is off, or null when it is available. */
export function resendDisabledReason(m: MemberRecord): string | null {
  if (resendItems(m).length > 0) return null
  return m.email ? 'Email to this member is blocked. Use Print instead.' : 'No email address. Use Print instead.'
}

/** The titles a resend can be about: every title the member has ever held. */
export function resendTitles(m: MemberRecord): { slug: string; name: string }[] {
  return m.titles.filter((t) => t.state !== 'never').map((t) => ({ slug: t.slug, name: t.name }))
}

/** One receipt letter per distinct last-payment order across the member's titles. */
export function receiptChoices(m: MemberRecord): { order_key: string; label: string }[] {
  const seen = new Map<string, string[]>()
  for (const t of m.titles) {
    if (!t.last_payment) continue
    const k = t.last_payment.order_key
    seen.set(k, [...(seen.get(k) ?? []), t.name])
  }
  return [...seen].map(([order_key, names]) => ({ order_key, label: `${names.join(' + ')} (order ${order_key})` }))
}

export interface LabelData { name: string; company: string | null; address: string[] }

/** The label's lines, top to bottom, with blanks dropped. */
export function labelLines(d: LabelData): string[] {
  return [d.name, d.company ?? '', ...d.address].map((l) => l.trim()).filter(Boolean)
}

export const sentMessage = (what: ResendWhat, to: string): string =>
  `${RESEND_ITEMS.find((i) => i.what === what)?.sent ?? 'Email'} sent to ${to}`
